import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, Send, CheckCircle2, Loader2, FileText } from "lucide-react";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

/**
 * Dialog used for both "Request Collaboration" and "Download Media Kit".
 * - download mode: POSTs to /api/media-kit/request, then renders an instant download
 *   button alongside a confirmation that the kit was also sent by email.
 * - collab mode: POSTs to /api/collaboration/request and shows a thanks state.
 */
export default function MediaKitDialog({ open, onOpenChange, mode = "collab" }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

  const isDownload = mode === "download";
  const title = isDownload ? "Download the media kit" : "Request a collaboration";
  const desc = isDownload
    ? "Enter your details and we will email you the kit. Your download will also appear here straight away."
    : "Tell us about your brand, event or campaign. Robin will reply within two working days.";

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    if (!isDownload && !form.message) {
      toast.error("Please add a short brief.");
      return;
    }
    setSubmitting(true);
    try {
      if (isDownload) {
        const { data } = await axios.post(`${API}/media-kit/request`, {
          name: form.name,
          email: form.email,
          company: form.company || null,
        });
        setPdfUrl(data?.media_kit_pdf_url || "");
        setSubmitted(true);
        toast.success("Media kit ready. Check your email shortly.");
      } else {
        await axios.post(`${API}/collaboration/request`, {
          name: form.name,
          email: form.email,
          company: form.company || null,
          message: form.message,
        });
        setSubmitted(true);
        toast.success("Enquiry sent. Robin will be in touch.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSubmitted(false);
    setSubmitting(false);
    setPdfUrl("");
    setForm({ name: "", email: "", company: "", message: "" });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 200); }}>
      <DialogContent
        data-testid="media-kit-dialog"
        className="bg-[#061826] border border-[#CAF0F8]/10 text-white max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-medium text-white">{title}</DialogTitle>
          <DialogDescription className="text-[#CAF0F8]/70 font-body">{desc}</DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-6 text-center space-y-5" data-testid="dialog-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#00B4D8]" strokeWidth={1.2} />
            {isDownload ? (
              <>
                <p className="font-heading text-xl text-white">Your media kit is ready.</p>
                <p className="text-[#CAF0F8]/70 text-sm">
                  We have also sent a copy to <span className="text-white">{form.email}</span>.
                </p>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="dialog-download-link"
                  className="inline-flex items-center justify-center gap-2 bg-[#00B4D8] text-[#050708] hover:bg-[#0077B6] hover:text-white font-medium rounded-full px-7 h-12 transition-colors"
                >
                  <FileText className="h-4 w-4" /> Download the PDF
                </a>
                <p className="text-[10px] text-[#CAF0F8]/40 pt-2">
                  Not seeing the email? Check your spam folder, or reuse the download button above.
                </p>
              </>
            ) : (
              <>
                <p className="font-heading text-xl text-white">Enquiry sent.</p>
                <p className="text-[#CAF0F8]/70 text-sm">
                  Robin will reply within two working days.
                </p>
                <Button
                  data-testid="dialog-close-btn"
                  onClick={() => onOpenChange(false)}
                  className="bg-[#00B4D8] text-[#050708] hover:bg-[#0077B6] hover:text-white"
                >
                  Close
                </Button>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div>
              <Label htmlFor="d-name" className="text-[#CAF0F8]/80 text-xs uppercase tracking-widest">Name</Label>
              <Input
                id="d-name"
                data-testid="dialog-name-input"
                value={form.name}
                onChange={handle("name")}
                placeholder="Your full name"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00B4D8]"
              />
            </div>
            <div>
              <Label htmlFor="d-email" className="text-[#CAF0F8]/80 text-xs uppercase tracking-widest">Email</Label>
              <Input
                id="d-email"
                data-testid="dialog-email-input"
                type="email"
                value={form.email}
                onChange={handle("email")}
                placeholder="you@brand.com"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00B4D8]"
              />
            </div>
            <div>
              <Label htmlFor="d-company" className="text-[#CAF0F8]/80 text-xs uppercase tracking-widest">Company or brand</Label>
              <Input
                id="d-company"
                data-testid="dialog-company-input"
                value={form.company}
                onChange={handle("company")}
                placeholder="Optional"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00B4D8]"
              />
            </div>
            {!isDownload && (
              <div>
                <Label htmlFor="d-msg" className="text-[#CAF0F8]/80 text-xs uppercase tracking-widest">Brief</Label>
                <Textarea
                  id="d-msg"
                  data-testid="dialog-message-input"
                  rows={4}
                  value={form.message}
                  onChange={handle("message")}
                  placeholder="Tell us about your campaign, timeline and goals."
                  className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00B4D8]"
                />
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="dialog-submit-btn"
              className="w-full bg-[#00B4D8] text-[#050708] hover:bg-[#0077B6] hover:text-white font-medium disabled:opacity-60"
            >
              {submitting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting</>
              ) : isDownload ? (
                <><Download className="mr-2 h-4 w-4" /> Send me the media kit</>
              ) : (
                <><Send className="mr-2 h-4 w-4" /> Send enquiry</>
              )}
            </Button>
            <p className="text-[10px] text-[#CAF0F8]/40 text-center">
              We respect your inbox. You can unsubscribe at any time.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
